import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import DisabledMask from 'components/DisabledMask';
import type { ScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { toggleHourTimestampFromMemory } from 'redux/modules/hours.actions';
import type {
  deleteEntryIds,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  toggleDisplayRuleForMode,
  toggleExpandEntryId,
  toggleExpandGroupRef,
} from 'redux/modules/timeline.actions';
import type { MemorySidebarUIProps } from 'redux/selectors/timeline.memories';
import MemoryGroupUpgradeState from 'routes/supercalendar/components/calendar/memory/MemoryGroupUpgradeState';
import type { MappedAction } from 'types/redux';
import type {
  ExtractTimelineMetaFromEntries,
  MemoryGroup as MemoryGroupType,
} from 'types/timeline';
import { MemoryGroup } from './MemoryGroup';
import { MemoryGroupEmptyState } from './MemoryGroupEmptyState';
import { MemoryGroupPlaceholder } from './MemoryGroupPlaceholder';
import styles from './MemoryInbox.module.scss';
import MemoryTimelineSubscriptionPrompt from './MemoryTimelineSubscriptionPrompt';

export interface MemoryInboxProps {
  toggleSelectEntryIds: (entry_ids: Array<number>) => void;
  toggleHourTimestampFromMemory: MappedAction<typeof toggleHourTimestampFromMemory>;
  predictEntryIdsToProjectId: (props: {
    entryIds: Array<number> | number;
    projectId: number;
  }) => unknown;
  toggleExpandGroupRef: MappedAction<typeof toggleExpandGroupRef>;
  toggleExpandEntryId: MappedAction<typeof toggleExpandEntryId>;
  toggleDisplayRuleForMode: MappedAction<typeof toggleDisplayRuleForMode>;
  markEntryIdsAsSpam: MappedAction<typeof markEntryIdsAsSpam>;
  markEntryIdsAsNotSpam: MappedAction<typeof markEntryIdsAsNotSpam>;
  deleteEntryIds: MappedAction<typeof deleteEntryIds>;
  groups: Array<MemoryGroupType>;
  meta: ExtractTimelineMetaFromEntries;
  ui: MemorySidebarUIProps;
  className?: string;
  hasExperimentalStyle?: boolean;
  setScrollPosition?: (scrollPosition: number) => void;
  initialScrollPosition?: number;
  isSubscriptionExpiredOrTrialEnded?: boolean;
}

export const MemoryInbox = (props: MemoryInboxProps) => {
  const {
    groups,
    ui,
    hasExperimentalStyle,
    setScrollPosition,
    initialScrollPosition = 0,
    isSubscriptionExpiredOrTrialEnded = false,
  } = props;
  const { emptyStateType, showEmptyState, isLoading, showPlaceholderMemories, showUpgradeState } =
    ui;
  const fallbackHeight = 64.6;
  const padding = 10;

  const [itemSizeList, setItemSize] = useState<Record<number, number>>(
    groups.reduce((prev, _, i) => ({ ...prev, [i]: fallbackHeight }), {})
  );

  const getItemSize = (index: number) => {
    const margin = hasExperimentalStyle ? 0 : 5;
    const size = itemSizeList[index] + margin;
    return size || fallbackHeight;
  };

  const parentRef = useRef<ScrollbarsRefType | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: groups.length,
    getScrollElement: () => parentRef.current?.view as Element | null,
    estimateSize: index => getItemSize(index),
    overscan: 20,
    paddingStart: padding,
    paddingEnd: padding,
  });

  useEffect(() => {
    setTimeout(() => {
      // force the scrollTop/offset of the virtualized list
      // react-virtual's initialOffset prop should handle this
      // but isn't working as expected as of 3.0-beta.18
      rowVirtualizer.scrollToOffset(initialScrollPosition);
      parentRef.current?.scrollTop(initialScrollPosition);
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  }, [initialScrollPosition]);

  const handleSetScrollPosition = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLElement>) => {
      setScrollPosition?.(currentTarget.scrollTop - padding);
    },
    [setScrollPosition]
  );

  const handleSetItemSize = (props: React.SetStateAction<Record<number, number>>) => {
    setItemSize(props);
    rowVirtualizer.measure();
  };

  function handleExpandGroupRef(groupRef: string) {
    props.toggleExpandGroupRef(groupRef);
  }

  const calculatedHeight = fallbackHeight * groups.length;
  const totalSize = rowVirtualizer.getTotalSize();
  const height = calculatedHeight > totalSize ? calculatedHeight : totalSize;

  return (
    <DisabledMask
      style={
        isSubscriptionExpiredOrTrialEnded
          ? {
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 38.21%)',
            }
          : undefined
      }
      wrapperStyle={
        isSubscriptionExpiredOrTrialEnded
          ? {
              pointerEvents: 'initial',
              filter: 'initial',
            }
          : undefined
      }
      active={ui.isDisabled}
    >
      {isSubscriptionExpiredOrTrialEnded && <MemoryTimelineSubscriptionPrompt />}
      <Scrollbars
        data-testid="memory_inbox"
        onScroll={handleSetScrollPosition}
        ref={parentRef}
        className={classNames(styles.container, props.className)}
      >
        <div style={{ height, width: '100%', position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map(({ index, key, size, start }) => {
            const group = groups[index];

            return (
              <div
                key={key}
                style={{
                  position: 'absolute',
                  top: start,
                  left: 0,
                  width: '100%',
                  height: `${size}px`,
                }}
              >
                <div style={{ top: `${start + 10}px` }}>
                  <MemoryGroup
                    operatorMode={props.ui.operatorMode}
                    hasEnabledTic={props.ui.hasEnabledTic}
                    toggleHourTimestampFromMemory={props.toggleHourTimestampFromMemory}
                    key={group.ref}
                    showDebugInfo={props.ui.showDebugInfo}
                    groupSection="inbox"
                    toggleExpandGroupRef={handleExpandGroupRef}
                    toggleExpandEntryId={props.toggleExpandEntryId}
                    toggleSelectEntryIds={props.toggleSelectEntryIds}
                    markEntryIdsAsSpam={props.markEntryIdsAsSpam}
                    markEntryIdsAsNotSpam={props.markEntryIdsAsNotSpam}
                    predictEntryIdsToProjectId={props.predictEntryIdsToProjectId}
                    deleteEntryIds={props.deleteEntryIds}
                    setItemSize={handleSetItemSize}
                    itemSizeList={itemSizeList}
                    groupIndex={index}
                    group={group}
                  />
                </div>
              </div>
            );
          })}
          {showPlaceholderMemories && (
            <MemoryGroupPlaceholder
              isLoading={isLoading}
              emptyType={emptyStateType}
              numItems={35}
            />
          )}
          {showUpgradeState && <MemoryGroupUpgradeState />}
          {showEmptyState && <MemoryGroupEmptyState ui={ui} />}
        </div>
      </Scrollbars>
    </DisabledMask>
  );
};
